/**
 * useBackgroundTask – Non-blocking background task queue with toast integration.
 *
 * Allows the UI to fire-and-forget async operations (save, publish, etc.)
 * while the user continues working.  Each task gets a unique ID so multiple
 * concurrent jobs never collide.
 *
 * Usage:
 *   const bg = useBackgroundTask();
 *   bg.run({ label: 'Burger Menu', work: () => saveDraft(id, data) });
 *
 * The hook manages its own lifecycle:
 *  – pending  → toast with spinner: "⏳ Saving Burger Menu…"
 *  – success  → toast updates:       "✅ Burger Menu saved"
 *  – error    → red toast with retry: "❌ Failed to save Burger Menu [Retry]"
 */

import { useCallback, useRef, useSyncExternalStore } from 'react';

// ── Types ──────────────────────────────────────────────────────────────────

export type TaskStatus = 'pending' | 'success' | 'error';

export interface BackgroundTask {
  id: string;
  label: string;
  status: TaskStatus;
  error?: string;
  startedAt: number;
}

interface RunOptions {
  /** Human-readable label shown in toast, e.g. "Burger Menu" */
  label: string;
  /** The async work to do (API call). */
  work: () => Promise<void>;
  /** Optional callback on success (e.g. reload list). */
  onSuccess?: () => void;
  /** Optional entity ID for inline "saving" badges on table rows. */
  entityId?: string;
}

// ── Singleton store (shared across all consumers) ──────────────────────────

let tasks: BackgroundTask[] = [];
let savingIds = new Set<string>();
const listeners = new Set<() => void>();

function notify() { listeners.forEach(fn => fn()); }

function getSnapshot(): BackgroundTask[] { return tasks; }
function getSavingIdsSnapshot(): Set<string> { return savingIds; }
function subscribe(cb: () => void) { listeners.add(cb); return () => { listeners.delete(cb); }; }

function addTask(task: BackgroundTask, entityId?: string) {
  tasks = [...tasks, task];
  if (entityId) { savingIds = new Set(savingIds); savingIds.add(entityId); }
  notify();
}

function updateTask(id: string, patch: Partial<BackgroundTask>) {
  tasks = tasks.map(t => t.id === id ? { ...t, ...patch } : t);
  notify();
}

function removeTask(id: string, entityId?: string) {
  tasks = tasks.filter(t => t.id !== id);
  if (entityId) { savingIds = new Set(savingIds); savingIds.delete(entityId); }
  notify();
}

// ── Hook ───────────────────────────────────────────────────────────────────

export function useBackgroundTask() {
  const currentTasks = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const currentSaving = useSyncExternalStore(subscribe, getSavingIdsSnapshot, getSavingIdsSnapshot);
  const retryMapRef = useRef<Map<string, RunOptions>>(new Map());

  const run = useCallback((opts: RunOptions) => {
    const id = crypto.randomUUID();
    const task: BackgroundTask = {
      id,
      label: opts.label,
      status: 'pending',
      startedAt: Date.now(),
    };
    retryMapRef.current.set(id, opts);
    addTask(task, opts.entityId);

    // Fire-and-forget
    opts.work()
      .then(() => {
        updateTask(id, { status: 'success' });
        opts.onSuccess?.();
        // Auto-clean after 3s
        setTimeout(() => { removeTask(id, opts.entityId); retryMapRef.current.delete(id); }, 3000);
      })
      .catch((err: any) => {
        updateTask(id, { status: 'error', error: err?.message || 'Unknown error' });
        // Keep around for retry; auto-clean after 15s if not retried
        if (opts.entityId) { savingIds = new Set(savingIds); savingIds.delete(opts.entityId); notify(); }
        setTimeout(() => { removeTask(id, opts.entityId); retryMapRef.current.delete(id); }, 15000);
      });

    return id;
  }, []);

  const retry = useCallback((taskId: string) => {
    const opts = retryMapRef.current.get(taskId);
    if (!opts) return;
    retryMapRef.current.delete(taskId);
    removeTask(taskId, opts.entityId);
    run(opts);
  }, [run]);

  const hasPending = currentTasks.some(t => t.status === 'pending');

  return {
    tasks: currentTasks,
    savingIds: currentSaving,
    run,
    retry,
    hasPending,
    /** True when a specific entity is actively being saved */
    isSaving: useCallback((entityId: string) => currentSaving.has(entityId), [currentSaving]),
  };
}

// ── Standalone accessor for non-hook contexts (e.g. beforeunload) ──────────
export function getActiveTasks(): BackgroundTask[] { return tasks; }
